/**
 * Appels d'API propres au site de gestion. Les fiches (organisation, lieux, équipements, tarifs,
 * photos) passent par les services du site public (@web/features/organizations/services).
 */
import { getApiBaseUrl, getApiVersion } from '@web/shared/api/runtimeConfig'
import { clearTokensIfUnauthorized, getAccessToken } from '@web/features/auth/utils/tokenStorage'
import { refreshAccessToken } from '@web/features/auth/utils/refreshAccessToken'

const API = `${getApiBaseUrl()}/${getApiVersion()}`

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const token = getAccessToken()
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  })
  // Jeton expiré : on le renouvelle une fois et on réessaie avant de déconnecter.
  if (res.status === 401 && !retried && (await refreshAccessToken())) return request<T>(path, init, true)
  if (res.status === 401) clearTokensIfUnauthorized()
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const detail = typeof body?.detail === 'string' ? body.detail : Object.values(body ?? {}).flat().join(' ')
    throw new ApiError(detail || `HTTP ${res.status}`, res.status)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export function photoThumbUrl(photoId: number, width = 320): string {
  return `${API}/photos/${photoId}/thumb/?w=${width}`
}

/* ---------- Statistiques ---------- */

export type InsightElement = {
  id: number
  name: string
  type: string
  status: string
  updated_at: string | null
  views: number
  views_prev: number
  like_count: number
  to_visit_count: number
  visited_count: number
  comment_count: number
  photo_count: number
  rating_avg: number | null
  rating_count: number
}

export type Insights = {
  organization_id: number
  period_days: number
  start: string
  end: string
  totals: Record<
    'views' | 'views_prev' | 'like_count' | 'to_visit_count' | 'visited_count' | 'comment_count' | 'photo_count',
    number
  >
  views_by_day: { day: string; count: number }[]
  elements: InsightElement[]
  recent_comments: {
    id: number
    location_type: string
    location_id: number
    location_name: string
    author: string
    body: string
    status: 'approved' | 'pending'
    created_at: string
  }[]
  recent_photos: {
    id: number
    author: string
    created_at: string
    location_type: string
    location_id: number
    location_name: string
  }[]
}

export function fetchInsights(orgId: number, days: number, signal?: AbortSignal, recent = 20): Promise<Insights> {
  return request(`/organizations/${orgId}/insights/?days=${days}&recent=${recent}`, { signal })
}

/* ---------- Carte : paquet de l'éditeur, chemins, équipements ---------- */

export type LatLng = { lat: number; lng: number }

export type OrgPath = {
  id: number
  path_type: string
  path_type_label: string
  name: string | null
  notes: string | null
  speed_limit_kmh: number | null
  access: PathAccess
  display: PathDisplay
  osm_sync_requested: boolean
  osm_highway: string
  usages: string[]
  coordinates: LatLng[]
  updated_at: string | null
}

export type PathAccess = 'public' | 'private'
export type PathDisplay = 'network' | 'organization'
export type Choice = { value: string; label: string; osm?: string }

export type OrgFacility = {
  id: number
  facility_type: string | null
  poi_type_id?: number | null
  poi_type?: { id: number; slug: string; name: string; parent_slug?: string | null; mdi_icon?: string | null; icon_color?: string | null } | null
  facility_type_label: string
  type_label?: string
  name: string | null
  notes: string | null
  info_text?: string | null
  access?: PathAccess
  location: { lat: number | null; lng: number | null }
}

export type EditorBundle = {
  id: number
  name: string
  geometry: {
    point: GeoJSON.Point | null
    path: GeoJSON.Geometry | null
    boundary: GeoJSON.Polygon | GeoJSON.MultiPolygon | null
    has_boundary: boolean
  } | null
  facilities: OrgFacility[]
  facility_types: Choice[]
  paths: OrgPath[]
  path_types: Choice[]
  path_access: Choice[]
  path_display: Choice[]
  path_usages: Choice[]
}

export function fetchEditorBundle(orgId: number, signal?: AbortSignal): Promise<EditorBundle> {
  return request(`/organizations/${orgId}/editor-bundle/`, { signal })
}

export type PathInput = {
  path_type: string
  name?: string | null
  notes?: string | null
  speed_limit_kmh?: number | null
  access?: PathAccess
  display?: PathDisplay
  osm_sync_requested?: boolean
  usages?: string[]
  coordinates: LatLng[]
}

export function createPath(orgId: number, data: PathInput): Promise<OrgPath> {
  return request(`/organizations/${orgId}/paths/`, { method: 'POST', body: JSON.stringify(data) })
}

export function updatePath(orgId: number, pathId: number, data: Partial<PathInput>): Promise<OrgPath> {
  return request(`/organizations/${orgId}/paths/${pathId}/`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deletePath(orgId: number, pathId: number): Promise<void> {
  return request(`/organizations/${orgId}/paths/${pathId}/`, { method: 'DELETE' })
}

export type FacilityInput = {
  facility_type?: string
  poi_type_id?: number | null
  name?: string | null
  notes?: string | null
  info_text?: string | null
  access?: PathAccess
  location: LatLng
}

export function createFacility(orgId: number, data: FacilityInput): Promise<OrgFacility> {
  return request(`/organizations/${orgId}/facilities/`, { method: 'POST', body: JSON.stringify(data) })
}

export function updateFacility(orgId: number, facilityId: number, data: Partial<FacilityInput>): Promise<OrgFacility> {
  return request(`/organizations/${orgId}/facilities/${facilityId}/`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteFacility(orgId: number, facilityId: number): Promise<void> {
  return request(`/organizations/${orgId}/facilities/${facilityId}/`, { method: 'DELETE' })
}

/* ---------- Sentiers du réseau public associés à l'organisation ---------- */

export type TrailProps = {
  id: number
  osm_id: number | null
  name: string
  custom: boolean
  track_label: string
  activities: string[]
  trail_types: string[]
  surface: string
  difficulty: string
  length_m: number | null
  linked?: boolean
  other_organizations?: string[]
}

export type TrailFeature = GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString | null, TrailProps>

export function fetchLinkedTrails(orgId: number, signal?: AbortSignal): Promise<GeoJSON.FeatureCollection<GeoJSON.LineString | GeoJSON.MultiLineString, TrailProps>> {
  return request(`/organizations/${orgId}/trails/`, { signal })
}

export function lookupTrail(orgId: number, ref: { osmId?: number | null; trailId?: number | null }): Promise<TrailFeature> {
  const q = ref.osmId ? `osm_id=${ref.osmId}` : `trail_id=${ref.trailId}`
  return request(`/organizations/${orgId}/trails/lookup/?${q}`)
}

export function linkTrail(orgId: number, trailId: number): Promise<TrailFeature> {
  return request(`/organizations/${orgId}/trails/`, { method: 'POST', body: JSON.stringify({ trail_id: trailId }) })
}

export function unlinkTrail(orgId: number, trailId: number): Promise<void> {
  return request(`/organizations/${orgId}/trails/${trailId}/`, { method: 'DELETE' })
}

/* ---------- POI publics de l'organisation ---------- */

export type OrgPoi = {
  id: number
  name: string
  slug: string | null
  status: string
  poi_type: { id: number; slug: string; name: string; parent_slug?: string | null; mdi_icon?: string | null; icon_color?: string | null } | null
  poi_type_id: number | null
  website: string | null
  osm_id: number | null
  osm_sync_requested: boolean
  location: LatLng | null
  updated_at: string | null
}

export function fetchPois(orgId: number, signal?: AbortSignal): Promise<{ results: OrgPoi[] }> {
  return request(`/organizations/${orgId}/pois/`, { signal })
}

export function createPoi(orgId: number, data: { poi_type_id: number; location: LatLng; name?: string | null; website?: string | null }): Promise<OrgPoi> {
  return request(`/organizations/${orgId}/pois/`, { method: 'POST', body: JSON.stringify(data) })
}

export function updatePoi(orgId: number, poiId: number, data: Partial<{ poi_type_id: number; location: LatLng; name: string; website: string | null; osm_sync_requested: boolean }>): Promise<OrgPoi> {
  return request(`/organizations/${orgId}/pois/${poiId}/`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deletePoi(orgId: number, poiId: number): Promise<void> {
  return request(`/organizations/${orgId}/pois/${poiId}/`, { method: 'DELETE' })
}

/** Équipement → POI public (l'équipement disparaît, ses photos suivent). */
export function facilityToPoi(orgId: number, facilityId: number): Promise<{ id: number; name: string; type: string }> {
  return request(`/organizations/${orgId}/facilities/${facilityId}/to-poi/`, { method: 'POST', body: '{}' })
}

/** POI public → équipement de l'organisation (le POI disparaît). */
export function poiToFacility(orgId: number, poiId: number): Promise<OrgFacility> {
  return request(`/organizations/${orgId}/facilities/from-poi/`, { method: 'POST', body: JSON.stringify({ poi_id: poiId }) })
}

/* ---------- Escalade (site → parois → voies) ---------- */

export type ClimbingStyle = 'sport' | 'trad' | 'toprope' | 'boulder' | 'ice' | 'mixed' | 'aid' | 'multipitch'
export const CLIMBING_STYLES: ClimbingStyle[] = ['sport', 'trad', 'toprope', 'boulder', 'ice', 'mixed', 'aid', 'multipitch']

export type ClimbingRoute = {
  id: number
  name: string
  grade: string
  grade_system: string
  styles: ClimbingStyle[]
  length_m: number | null
  pitches: number | null
  bolts: number | null
  description: string
  url: string
  location: LatLng | null
  from_osm: boolean
  source: string
}

export type ClimbingCrag = {
  id: number
  name: string
  description: string
  url: string
  location: LatLng | null
  cliff_lines: LatLng[][]
  from_osm: boolean
  source: string
  routes: ClimbingRoute[]
}

export type ClimbingSite = {
  id: number
  name: string
  description: string
  poi_id: number | null
  location: LatLng | null
  from_osm: boolean
  source: string
  crags: ClimbingCrag[]
}

export type RouteInput = {
  name: string
  grade: string
  styles: ClimbingStyle[]
  length_m: number | null
  pitches: number | null
  bolts: number | null
  description: string
  location: LatLng
}

export type CragInput = { name: string; description: string; url: string; location: LatLng; cliff_lines: LatLng[][] }

/** Le site d'escalade d'un POI : relié, ou importé d'OSM juste à côté (poi_id vide), ou aucun. */
export function fetchClimbing(orgId: number, poiId: number, signal?: AbortSignal): Promise<{ site: ClimbingSite | null }> {
  return request(`/organizations/${orgId}/climbing/?poi=${poiId}`, { signal })
}

export function createClimbingSite(orgId: number, poiId: number): Promise<ClimbingSite> {
  return request(`/organizations/${orgId}/climbing/sites/`, { method: 'POST', body: JSON.stringify({ poi_id: poiId }) })
}

/** Relie un site importé au POI (pour le gérer), ou le renomme. */
export function updateClimbingSite(orgId: number, siteId: number, data: Partial<{ poi_id: number; name: string; description: string }>): Promise<ClimbingSite> {
  return request(`/organizations/${orgId}/climbing/sites/${siteId}/`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function createCrag(orgId: number, data: Partial<CragInput> & { site_id: number; name: string }): Promise<ClimbingCrag> {
  return request(`/organizations/${orgId}/climbing/crags/`, { method: 'POST', body: JSON.stringify(data) })
}

export function updateCrag(orgId: number, cragId: number, data: Partial<CragInput>): Promise<ClimbingCrag> {
  return request(`/organizations/${orgId}/climbing/crags/${cragId}/`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteCrag(orgId: number, cragId: number): Promise<void> {
  return request(`/organizations/${orgId}/climbing/crags/${cragId}/`, { method: 'DELETE' })
}

export function createRoute(orgId: number, data: RouteInput & { crag_id: number }): Promise<ClimbingRoute> {
  return request(`/organizations/${orgId}/climbing/routes/`, { method: 'POST', body: JSON.stringify(data) })
}

export function updateRoute(orgId: number, routeId: number, data: Partial<RouteInput & { crag_id: number }>): Promise<ClimbingRoute> {
  return request(`/organizations/${orgId}/climbing/routes/${routeId}/`, { method: 'PATCH', body: JSON.stringify(data) })
}

export function deleteRoute(orgId: number, routeId: number): Promise<void> {
  return request(`/organizations/${orgId}/climbing/routes/${routeId}/`, { method: 'DELETE' })
}

/* ---------- Périmètre de l'organisation ---------- */

/** Une zone du parc : son contour, et ses trous (enclaves) gardés tels quels. */
export type BoundaryZone = { outer: LatLng[]; holes: LatLng[][] }
export type BoundaryData = { zones: BoundaryZone[]; osm: { id: number; type: string | null; name?: string } | null }

export function fetchBoundary(orgId: number, signal?: AbortSignal): Promise<BoundaryData> {
  return request(`/organizations/${orgId}/boundary/`, { signal })
}

export function saveBoundary(orgId: number, zones: BoundaryZone[]): Promise<BoundaryData> {
  return request(`/organizations/${orgId}/boundary/`, { method: 'PUT', body: JSON.stringify({ zones }) })
}

/** Contour lu dans OSM, sans rien enregistrer (aperçu). Sans référence : l'identifiant OSM de l'organisation. */
export function previewBoundaryFromOsm(orgId: number, osm?: string): Promise<BoundaryData> {
  return request(`/organizations/${orgId}/boundary/osm-preview/`, { method: 'POST', body: JSON.stringify({ osm: osm || null }) })
}

/* ---------- Emplacements de camping ---------- */

/** Modifier un emplacement : surtout son camping parent (null : aucun). */
export function updatePitch(
  orgId: number,
  pitchId: number,
  data: Partial<{ camping_id: number | null; name: string; rate_tag: string; location: LatLng }>,
): Promise<unknown> {
  return request(`/organizations/${orgId}/pitches/${pitchId}/`, { method: 'PATCH', body: JSON.stringify(data) })
}

/* ---------- Sentiers officiels (Itinerary / Track rattachés au parc) ---------- */

export type OfficialKind = 'itinerary' | 'track'

export type OfficialTrail = {
  kind: OfficialKind
  id: number
  name: string
  public_id: string
  slug: string | null
  ref: string | null
  visibility: string
  moderation_status: string
  is_official: boolean
  distance_km: number | null
  poi_count: number
  duration: number
  wizard_step: string | null
  thumbnail_url: string | null
  created_at: string | null
  updated_at: string | null
  relation?: 'within' | 'crosses'
}

export function fetchOfficialTrails(orgId: number, signal?: AbortSignal): Promise<{ results: OfficialTrail[] }> {
  return request(`/organizations/${orgId}/official-itineraries/`, { signal })
}

export function fetchAttachableTrails(orgId: number, q = '', signal?: AbortSignal): Promise<{ results: OfficialTrail[] }> {
  const query = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
  return request(`/organizations/${orgId}/attachable-itineraries/${query}`, { signal })
}

export function attachOfficialTrail(orgId: number, kind: OfficialKind, id: number): Promise<OfficialTrail> {
  return request(`/organizations/${orgId}/official-itineraries/`, {
    method: 'POST',
    body: JSON.stringify({ kind, id }),
  })
}

export function detachOfficialTrail(orgId: number, kind: OfficialKind, id: number): Promise<void> {
  return request(`/organizations/${orgId}/official-itineraries/${kind}/${id}/`, { method: 'DELETE' })
}

export function renameOfficialTrail(orgId: number, kind: OfficialKind, id: number, name: string): Promise<OfficialTrail> {
  return request(`/organizations/${orgId}/official-itineraries/${kind}/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  })
}

/* ---------- Tarifs d'accès et location (montants : chaînes décimales, avant taxes) ---------- */

export type TariffListItem = {
  id: number
  slug: string
  name: string
  name_en: string
  pending: boolean
  kind?: 'age' | 'status' | 'group'
  default_unit?: string
  default_validity?: string
  category?: string
  synonyms?: string[]
  minutes?: number | null
}
export type TariffScaleItem = { id: number; category_id: number | null; age_min: number | null; age_max: number | null; kind: string; label: string }
export type TariffCell = { category_id: number | null; amount: string | null; amount_with_tax: string | null; on_request: boolean }
export type AccessLine = {
  id: number
  product_id: number
  product: string
  unit: string
  activity_id: number | null
  season: string
  validity: string
  validity_days: number | null
  year: number | null
  date_from: string | null
  date_to: string | null
  starting_from: boolean
  note: string
  cells: TariffCell[]
}
export type RentalPriceCell = TariffCell & { duration_id: number }
export type RentalOfferRow = {
  id: number
  item_id: number
  item: string
  variant: string
  length_ft: string | null
  capacity: number | null
  model: string
  quantity: number | null
  deposit: string | null
  min_age: number | null
  included: string
  reservation_required: boolean
  season: string
  note: string
  prices: RentalPriceCell[]
}
export type Tariffs = {
  settings: { prices_include_tax: boolean; payment_note: string }
  tax_rate: string
  lists: { categories: TariffListItem[]; products: TariffListItem[]; items: TariffListItem[]; durations: TariffListItem[] }
  choices: { units: [string, string][]; seasons: [string, string][]; validities: [string, string][]; kinds: [string, string][]; equipment_categories: [string, string][] }
  scale: TariffScaleItem[]
  scale_problems: string[]
  access_lines: AccessLine[]
  rental_offers: RentalOfferRow[]
}
export type CellInput = { category_id: number | null; amount: string | null; on_request: boolean }
export type QuoteResult = {
  lines: { line_id: number; product: string; unit: string; amount: string; detail: string[]; missing: string[]; on_request: boolean }[]
  before_tax: string
  tax_rate: string
  taxes: string
  total: string
}

const tariffs = (orgId: number, path = '') => `/organizations/${orgId}/tariffs/${path}`

export function fetchTariffs(orgId: number, signal?: AbortSignal): Promise<Tariffs> {
  return request(tariffs(orgId), { signal })
}
export function saveTariffSettings(orgId: number, data: Partial<Tariffs['settings']>): Promise<Tariffs> {
  return request(tariffs(orgId, 'settings/'), { method: 'PATCH', body: JSON.stringify(data) })
}
export function saveTariffScale(orgId: number, categories: { id?: number; category_id: number | null; age_min: number | null; age_max: number | null }[]): Promise<Tariffs> {
  return request(tariffs(orgId, 'scale/'), { method: 'PUT', body: JSON.stringify({ categories }) })
}
/** Demande d'ajout à une liste commune (utilisable tout de suite par l'organisation). */
export function requestTariffListItem(orgId: number, list: keyof Tariffs['lists'], name: string, extra: { kind?: string; category?: string } = {}): Promise<TariffListItem> {
  return request(tariffs(orgId, 'requests/'), { method: 'POST', body: JSON.stringify({ list, name, ...extra }) })
}
export function saveAccessLine(orgId: number, id: number | null, data: Partial<Omit<AccessLine, 'id' | 'cells' | 'product'>> & { cells?: CellInput[] }): Promise<Tariffs> {
  return request(tariffs(orgId, id ? `access/${id}/` : 'access/'), { method: id ? 'PATCH' : 'POST', body: JSON.stringify(data) })
}
export function deleteAccessLine(orgId: number, id: number): Promise<void> {
  return request(tariffs(orgId, `access/${id}/`), { method: 'DELETE' })
}
export function saveRentalOffer(orgId: number, id: number | null, data: Partial<Omit<RentalOfferRow, 'id' | 'prices' | 'item'>> & { prices?: (CellInput & { duration_id: number })[] }): Promise<Tariffs> {
  return request(tariffs(orgId, id ? `rental/${id}/` : 'rental/'), { method: id ? 'PATCH' : 'POST', body: JSON.stringify(data) })
}
export function deleteRentalOffer(orgId: number, id: number): Promise<void> {
  return request(tariffs(orgId, `rental/${id}/`), { method: 'DELETE' })
}
export function quoteTariffs(orgId: number, party: { age?: number | null; status?: string; label?: string }[], vehicles = 0, boats = 0): Promise<QuoteResult> {
  return request(tariffs(orgId, 'quote/'), { method: 'POST', body: JSON.stringify({ party, vehicles, boats }) })
}

/* ---------- Accès de la clientèle (groupes, membres, codes QR) ---------- */

export type ClientAccessMember = {
  id: number
  user: { id: number; username: string; email: string; name: string }
  via_code: string | null
  granted_by: string | null
  created_at: string | null
  expires_at: string | null
  current: boolean
}
export type ClientAccessCode = {
  id: number
  code: string
  duration: string
  max_uses: number | null
  uses: number
  expires_at: string | null
  is_active: boolean
  /** Pourquoi le code ne sert plus : inactive, expired, used_up, group_closed ; null = utilisable. */
  problem: string | null
  created_at: string | null
}
export type ClientAccessGroup = {
  id: number
  name: string
  description: string
  is_active: boolean
  ends_on: string | null
  members: ClientAccessMember[]
  codes: ClientAccessCode[]
}
export type ClientAccess = { groups: ClientAccessGroup[]; durations: [string, string][] }

const clientAccess = (orgId: number, path = '') => `/organizations/${orgId}/client-access/${path}`

export function fetchClientAccess(orgId: number, signal?: AbortSignal): Promise<ClientAccess> {
  return request(clientAccess(orgId), { signal })
}
export function saveClientAccessGroup(orgId: number, id: number | null, data: Partial<Pick<ClientAccessGroup, 'name' | 'description' | 'is_active' | 'ends_on'>>): Promise<ClientAccess> {
  return request(clientAccess(orgId, id ? `groups/${id}/` : 'groups/'), { method: id ? 'PATCH' : 'POST', body: JSON.stringify(data) })
}
export function deleteClientAccessGroup(orgId: number, id: number): Promise<ClientAccess> {
  return request(clientAccess(orgId, `groups/${id}/`), { method: 'DELETE' })
}
/** Ajoute un compte au groupe (nom d'utilisateur ou courriel). */
export function addClientAccessMember(orgId: number, groupId: number, identifier: string, expiresAt: string | null): Promise<ClientAccess> {
  return request(clientAccess(orgId, `groups/${groupId}/members/`), { method: 'POST', body: JSON.stringify({ identifier, expires_at: expiresAt }) })
}
export function removeClientAccessMember(orgId: number, groupId: number, grantId: number): Promise<ClientAccess> {
  return request(clientAccess(orgId, `groups/${groupId}/members/${grantId}/`), { method: 'DELETE' })
}
export function createClientAccessCode(orgId: number, groupId: number, data: { duration: string; max_uses: number | null; expires_at: string | null }): Promise<ClientAccess> {
  return request(clientAccess(orgId, `groups/${groupId}/codes/`), { method: 'POST', body: JSON.stringify(data) })
}
export function setClientAccessCodeActive(orgId: number, groupId: number, codeId: number, isActive: boolean): Promise<ClientAccess> {
  return request(clientAccess(orgId, `groups/${groupId}/codes/${codeId}/`), { method: 'PATCH', body: JSON.stringify({ is_active: isActive }) })
}
